/**
 * Step 3: Pay
 * Modal overlay on the plan screen using ShadCN Dialog.
 * Order summary, guarantee, card input (Stripe placeholder), lightweight account creation.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Lock,
  CreditCard,
  Shield,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
} from 'lucide-react';
import mediaPlanApi from '@/services/mediaPlanApi';
import { toast } from 'sonner';

export default function PaymentModal({ plan, business, user, onClose, onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [zip, setZip] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [useGoogleSSO, setUseGoogleSSO] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  /** Validate all form fields and return true if valid */
  const validate = () => {
    const errors = {};

    // Account fields only required when no user and not using Google SSO
    if (!user && !useGoogleSSO) {
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.email = 'Enter a valid email address';
      }
      if (!password || password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }
    }

    // Card number: 16 digits after stripping spaces
    const rawCard = cardNumber.replace(/\s/g, '');
    if (rawCard.length !== 16 || !/^\d{16}$/.test(rawCard)) {
      errors.cardNumber = 'Enter a valid 16-digit card number';
    }

    // Expiry: MM/YY format, month 01-12
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry)) {
      errors.expiry = 'Use MM/YY format (01-12)';
    }

    // CVC: 3-4 digits
    if (!/^\d{3,4}$/.test(cvc)) {
      errors.cvc = '3-4 digits required';
    }

    // ZIP: 5 digits
    if (!/^\d{5}$/.test(zip)) {
      errors.zip = '5 digits required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /** Check whether the submit button should be enabled */
  const isFormValid = () => {
    const needsAccount = !user && !useGoogleSSO;
    const hasAccount = !needsAccount || (
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && password.length >= 8
    );
    const rawCard = cardNumber.replace(/\s/g, '');
    return (
      hasAccount &&
      /^\d{16}$/.test(rawCard) &&
      /^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry) &&
      /^\d{3,4}$/.test(cvc) &&
      /^\d{5}$/.test(zip)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsProcessing(true);
    try {
      const paymentData = {
        email: user?.email || email,
        cardLast4: cardNumber.replace(/\s/g, '').slice(-4),
        cardNumber: cardNumber.replace(/\s/g, ''),
        expiry,
        cvc,
        zip,
      };
      const response = await mediaPlanApi.createPayment(plan.id, paymentData);
      toast.success('Payment processed!');
      onSuccess(response.campaignId);
    } catch (err) {
      toast.error(err.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\D/g, '').slice(0, 16);
    return v.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\D/g, '').slice(0, 4);
    if (v.length >= 2) return v.slice(0, 2) + '/' + v.slice(2);
    return v;
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto p-0 gap-0">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-3">
              <img
                src="/assets/logos/cornercast-logo-primary.svg"
                alt="CornerCast"
                className="h-7"
              />
            </div>
            <DialogTitle className="sr-only">Complete your order</DialogTitle>
            <DialogDescription className="sr-only">
              Enter payment details to start generating your campaign ads.
            </DialogDescription>
          </DialogHeader>

          {/* Order summary */}
          <Card className="bg-muted border-0 shadow-none">
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-sm font-semibold">{plan.name}</div>
                  <div className="text-xs text-muted-foreground">{business.name}</div>
                </div>
                <div className="text-xl font-bold">${plan.budget.toLocaleString()}</div>
              </div>
              <div className="flex gap-4 text-[11px] text-muted-foreground">
                <span>{plan.duration}</span>
                <span>{plan.variants} ad variant{plan.variants > 1 ? 's' : ''}</span>
                <span>{plan.format}</span>
              </div>
            </CardContent>
          </Card>

          {/* Guarantee */}
          <div className="flex items-center gap-2.5 mt-3 px-3 py-2.5 bg-emerald-50/50 border border-emerald-100 rounded-lg">
            <Shield className="h-4 w-4 text-emerald-500 shrink-0" />
            <div className="text-xs text-emerald-700 leading-snug">
              <strong>Full refund guarantee</strong> on your first campaign if you're not satisfied.
            </div>
          </div>
        </div>

        <Separator />

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5">
          {/* Account section */}
          <div className="mb-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Account</div>

            {user ? (
              <div className="flex items-center gap-3 p-3 bg-emerald-50/50 border border-emerald-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Paying as</div>
                  <div className="text-xs text-muted-foreground">{user.email || user.name || 'Your account'}</div>
                </div>
              </div>
            ) : !useGoogleSSO ? (
              <div className="space-y-2.5">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Email</label>
                  <Input
                    type="email"
                    placeholder="you@business.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  {validationErrors.email && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {validationErrors.password && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.password}</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Separator className="flex-1" />
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase">or</span>
                  <Separator className="flex-1" />
                </div>

                <button
                  type="button"
                  className="w-full h-9 border border-border rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-muted transition-colors"
                  onClick={() => setUseGoogleSSO(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  Continue with Google
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-emerald-50/50 border border-emerald-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Google account connected</div>
                  <div className="text-xs text-muted-foreground">demo@business.com</div>
                </div>
                <button
                  type="button"
                  className="text-xs text-coral hover:text-coral/80 transition-colors"
                  onClick={() => setUseGoogleSSO(false)}
                >
                  Change
                </button>
              </div>
            )}
          </div>

          {/* Payment section */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
              <Lock className="h-3 w-3" />
              Payment
            </div>

            <div className="space-y-2.5">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Card number</label>
                <div className="relative">
                  <Input
                    placeholder="1234 5678 9012 3456"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                    required
                    className="pr-10"
                  />
                  <CreditCard className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                </div>
                {validationErrors.cardNumber && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.cardNumber}</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2.5">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Expiry</label>
                  <Input
                    placeholder="MM/YY"
                    value={expiry}
                    onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                    required
                  />
                  {validationErrors.expiry && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.expiry}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">CVC</label>
                  <Input
                    type="text"
                    placeholder="123"
                    value={cvc}
                    onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    required
                  />
                  {validationErrors.cvc && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.cvc}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">ZIP</label>
                  <Input
                    placeholder="80203"
                    value={zip}
                    onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    required
                  />
                  {validationErrors.zip && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.zip}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            className="w-full mt-5 bg-coral hover:bg-coral/90 text-white shadow-[0_2px_10px_rgba(255,107,107,0.35)] h-11 text-sm font-semibold"
            disabled={isProcessing || !isFormValid()}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing payment...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" />
                Pay ${plan.budget.toLocaleString()} and Generate Ads
              </>
            )}
          </Button>

          <div className="flex items-center justify-center gap-2 mt-3 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3" />
            Secured by Stripe. Your data is encrypted.
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
